<script setup lang="ts">
import {OpenaiCharacter} from "@/api/dx-backend";
import {computed} from "vue";
import {avatarPlaceholderUrl} from '@/utils/avatarPlaceholder';
import {fallbackOnError} from '@/utils/imageFallback';
import FlowSignMark from '@/components/CharacterToken/FlowSignMark.vue';
interface Props {
  character: OpenaiCharacter
  selected: boolean
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'copy-id': [characterId: string];
  'filter-by-position': [character: OpenaiCharacter];
  'filter-by-organisation': [character: OpenaiCharacter];
}>();

const avatarUrl = computed(() => {
  return (
    props.character?.biography?.avatar ||
    avatarPlaceholderUrl(props.character?.biography?.gender)
  );
});

const onAvatarError = (event: Event) =>
  fallbackOnError(event, avatarPlaceholderUrl(props.character?.biography?.gender));

// Fast action handlers
const handleCopyId = (event: Event) => {
  event.stopPropagation();
  emit('copy-id', props.character.id);
};

const handleFilterByPosition = (event: Event) => {
  event.stopPropagation();
  emit('filter-by-position', props.character);
};

const handleFilterByOrganisation = (event: Event) => {
  event.stopPropagation();
  emit('filter-by-organisation', props.character);
};
</script>

<template>
  <div class="selector-card" :class="{selected: props.selected}">
    <div class="avatar-container">
      <img :src="avatarUrl" :alt="props.character.name" class="avatar" @error="onAvatarError"/>
      <FlowSignMark v-if="props.character.game_master_mode" :size="20"/>

      <!-- Fast Actions Overlay -->
      <div class="fast-actions">
        <button
          class="action-btn"
          title="Copy Character ID"
          @click="handleCopyId"
        >
          📋
        </button>
        <button
          class="action-btn"
          title="Filter by Position"
          @click="handleFilterByPosition"
          v-if="props.character.position_id"
        >
          🎯
        </button>
        <button
          class="action-btn"
          title="Filter by Organization"
          @click="handleFilterByOrganisation"
          v-if="props.character.organization?.id"
        >
          🏛️
        </button>
      </div>
    </div>
    <div class="character-info">
      <h3 class="character-name">{{ props.character.name }}</h3>
      <p class="character-rank">{{ props.character.rank.name }}</p>
    </div>
  </div>
</template>

<style scoped>
.selector-card {
  aspect-ratio: 4 / 5;
  display: flex;
  flex-direction: column;
  padding: var(--gm-gap-sm);
  border: 2px solid transparent;
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background: var(--gm-surface-1);
  height: 100%;
}

.selector-card:hover {
  background: var(--gm-surface-2);
}

.selector-card.selected {
  border-color: var(--gm-accent);
  background: var(--gm-accent-soft);
}

.avatar-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--gm-gap-xs);
  overflow: hidden;
  border-radius: var(--gm-radius-sm);
  position: relative;
}

.avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--gm-radius-sm);
}

.character-info {
  flex-shrink: 0;
  text-align: center;
  padding-top: 0.175rem;
}

.character-name {
  margin: 0 0 0.175rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--gm-font-display);
  color: var(--gm-accent);
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.character-rank {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 400;
  font-family: var(--gm-font-body);
  color: var(--gm-text-muted);
  line-height: 1.1;
}

/* Fast Actions Overlay */
.fast-actions {
  position: absolute;
  top: var(--gm-gap-xs);
  right: var(--gm-gap-xs);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: var(--gm-gap-xs);
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid var(--gm-border);
  border-radius: var(--gm-radius-sm);
  background: var(--gm-surface-sunken);
  color: var(--gm-text);
  font-size: 0.75rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.action-btn:hover {
  border-color: var(--gm-accent);
  background: var(--gm-accent-soft);
}
</style>
